import { FilterExpression } from '@infuse/api/common';
import { Filter, FilterConfiguration } from '@infuse/ui/domain';

export class FilterCollection {
  private readonly FILTER_JOINER = '&';
  private readonly NEGATED_PREFIX = '!';
  private readonly FILTER_OPTION_JOINER = '.';
  private readonly VALUE_ASSIGNMENT = '=';
  private readonly VALUE_SEPARATOR = ':~:';
  private readonly DECODE_REGEX = new RegExp(
    `(?<filterName>\\w+?)\\.(?<optionName>[^${
      this.FILTER_JOINER + this.VALUE_ASSIGNMENT + this.NEGATED_PREFIX
    }]+)(?<negated>${this.NEGATED_PREFIX}?)${this.VALUE_ASSIGNMENT}?(?<value>.*)`
  );

  private _filters: Filter[];

  constructor() {
    this._filters = [];
  }

  get filters(): Filter[] {
    return this._filters;
  }

  set filters(filters: Filter[]) {
    this._filters = filters;
  }

  clear() {
    this._filters = [];
  }

  toFilterExpression(): FilterExpression {
    // if there are no filters, return an empty FilterExpression
    if (!this._filters.length) {
      return new FilterExpression();
    }

    // Exclude filters that do not use Umbrella (like the AbacScopeFilter)
    const activeFilters = this._filters.filter((f) => !f.config.excludeFromUmbrella);
    if (!activeFilters.length) {
      return new FilterExpression();
    }

    // separate the filters by configuration name
    const separatedFilters = this.separateFiltersByConfigName(activeFilters);

    // create a combined filter expression for each configuration name with 'and' or 'or' depending on negation
    const separatedExpressions = Object.keys(separatedFilters).map((configName) =>
      this.createCombinedFilterExpression(separatedFilters[configName])
    );

    // combine all the separated expressions with 'and'
    return separatedExpressions.reduce((acc, cur) => acc.and(cur), new FilterExpression());
  }

  add(filter: Filter) {
    if (filter.config.singleton) {
      // Overwrite current active filter with the same name
      this._filters = this._filters.filter((f) => f.config.name !== filter.config.name);
    }

    const alreadyIncludesFilter = this._filters.find(
      (f: Filter) =>
        f.config.name === filter.config.name &&
        f.searchQuery === filter.searchQuery &&
        f.selectedOption.name === filter.selectedOption.name
    ); // only replace filter when the same filter with inverse negation already exists

    if (alreadyIncludesFilter) {
      this.remove(filter);
    }

    this._filters.push(filter);
  }

  remove(filter: Filter) {
    const index = this._filters.findIndex(
      (f: Filter) =>
        f.config.name === filter.config.name &&
        f.selectedOption.name === filter.selectedOption.name &&
        f.searchQuery === filter.searchQuery
    );
    if (index !== -1) {
      this._filters.splice(index, 1);
    }
  }

  urlEncode(): string {
    const encodedFilters: string[] = [];
    this._filters.forEach((f: Filter) => {
      let encodedFilter = `${f.config.name}${this.FILTER_OPTION_JOINER}${f.selectedOption.name}${
        f.negated ? this.NEGATED_PREFIX : ''
      }`;
      if (f.searchQuery) {
        let value = encodeURIComponent(f.searchQuery);
        if (f.displayValue) {
          value += `${this.VALUE_SEPARATOR}${encodeURIComponent(f.displayValue)}`;
        }
        encodedFilter += `=${value}`;
      }
      encodedFilters.push(encodedFilter);
    });

    return encodedFilters.length > 0 ? encodedFilters.join(this.FILTER_JOINER) : '';
  }

  decodeAndAddFilters(urlEncodedFilters: string, filterConfigs: FilterConfiguration[], clearExisting = true) {
    if (urlEncodedFilters === '') {
      return;
    }

    if (clearExisting) {
      this.clear();
    }

    const encodedFilters = decodeURIComponent(urlEncodedFilters);
    const filters = encodedFilters.split(this.FILTER_JOINER);
    filters.forEach((f) => {
      const match = this.DECODE_REGEX.exec(f);
      if (!match || !match.groups) {
        console.warn(`Could not decode filter ${f}`);
        return;
      }

      const { filterName, optionName, negated, value } = match.groups;
      const config = filterConfigs.find((fc: FilterConfiguration) => fc.name === filterName);
      const selectedOption = config?.options.find((o) => o.name === optionName);
      if (!config || !selectedOption) {
        console.warn(`Could not find filter config and/or option for ${filterName}.${optionName}`);
        return;
      }

      let searchQuery = value ?? '';
      let displayValue: string | undefined;

      if (searchQuery.includes(this.VALUE_SEPARATOR)) {
        const parts = searchQuery.split(this.VALUE_SEPARATOR);
        searchQuery = parts[0];
        displayValue = parts[1];
      }

      const filter = {
        config,
        selectedOption,
        negated: negated === this.NEGATED_PREFIX,
        searchQuery,
        displayValue
      };
      this.add(filter);
    });
  }

  private separateFiltersByConfigName(filters: Filter[]): Record<string, Filter[]> {
    const separatedFilters: Record<string, Filter[]> = {};

    filters.forEach((filter) => {
      const configName = filter.config.name;

      if (!separatedFilters[configName]) {
        separatedFilters[configName] = [];
      }

      separatedFilters[configName].push(filter);
    });

    return separatedFilters;
  }

  // create a combined filter expression for each configuration name with 'and' or 'or' depending on negation
  private createCombinedFilterExpression(filters: Filter[]): FilterExpression {
    const nonNegatedFilters = filters.filter((f) => !f.negated);
    const negatedFilters = filters.filter((f) => f.negated);

    const expressions = nonNegatedFilters.map(this.createFilterExpression);
    const negatedExpressions = negatedFilters.map(this.createFilterExpression);

    const combinedExpressions = expressions.reduce((acc, cur) => acc.or(cur), new FilterExpression());
    return negatedExpressions.reduce((acc, cur) => acc.and(cur), combinedExpressions);
  }

  private createFilterExpression(filter: Filter): FilterExpression {
    if (filter.negated) {
      if (filter.selectedOption.toNegatedUmbrellaExpression) {
        return filter.selectedOption.toNegatedUmbrellaExpression(filter.searchQuery);
      } else {
        return new FilterExpression().not(filter.selectedOption.toUmbrellaExpression(filter.searchQuery));
      }
    } else {
      return filter.selectedOption.toUmbrellaExpression(filter.searchQuery);
    }
  }
}
